import Box from "@mui/material/Box";
import HoverAuthorizeTooltip from "components/authorize/AuthorizeTooltip";
import { Col, Form, Button, Row } from "react-bootstrap";
import { Event } from "types/Event";
import {
    useState,
    KeyboardEvent,
    memo,
    useMemo,
    useCallback,
    Dispatch,
    SetStateAction,
    useEffect,
} from "react";
import eventService from "service/eventService";
import editIcon from "assets/svg/edit.svg";
import { showErrorAlert, showSuccessAlert, getAPIError } from "utils/alert";
import { isHttpSuccess } from "utils/functions";
import { EVENT_ENABLED_EDIT_MESSAGE, EventNameMaxCharacter } from "constant";
import { ACTION_FAIL_MESSAGE } from "constant/messages";
import { Typography } from "@mui/material";
import { Registry } from "types/Gateways";
import { getOneRegistry } from "service/gatewayService";

export type EventNameProps = {
    eventName: string;
    isActive: boolean;
    eventUUID: string;
    gateway_id?: string;
    setEventDetail: Dispatch<SetStateAction<Event | undefined>>;
};

export const EventName = memo(
    ({
        eventName,
        isActive,
        eventUUID,
        gateway_id,
        setEventDetail,
    }: EventNameProps) => {
        const [editableName, setEditableName] = useState(eventName);
        const [isEditMode, setIsEditMode] = useState(false);
        const [isNameUpdating, setIsNameUpdating] = useState(false);
        const [gateway, setGateway] = useState<Registry | undefined>(undefined);

        useEffect(() => {
            if (gateway_id) {
                getOneRegistry(gateway_id).then((res) => {
                    if (isHttpSuccess(res.status)) {
                        setGateway(res.data);
                    }
                });
            }
        }, [gateway_id]);

        const handleEditClick = useCallback(() => {
            // Condition checking: if event is active, show error and return
            if (isActive) {
                showErrorAlert({
                    message: EVENT_ENABLED_EDIT_MESSAGE,
                });
                return;
            }

            // Enable edit mode
            setIsEditMode(true);
        }, [isActive]);

        const handleNameUpdate = useCallback(async () => {
            // Validation for blank names and special chars
            const editableNameTrim = String(editableName).trim();
            setEditableName(editableNameTrim);

            // Check if name hasn't changed
            if (editableNameTrim === eventName) {
                setIsEditMode(false);
                return;
            }

            // Validate name length
            if (
                !editableNameTrim ||
                editableNameTrim.length > EventNameMaxCharacter
            ) {
                showErrorAlert({
                    title: "Invalid Event Name",
                    message: `Event name must be 1-${EventNameMaxCharacter} characters long and not contain leading or trailing spaces or non-English characters.`,
                });
                return;
            }

            setIsNameUpdating(true);
            const response = await eventService.updateEvent(eventUUID, {
                name: editableNameTrim,
            });
            setIsNameUpdating(false);

            if (isHttpSuccess(response.status)) {
                showSuccessAlert({
                    message: "Event name has been updated successfully.",
                });
                setIsEditMode(false);
                setEventDetail((prev) => {
                    if (!prev) return prev;
                    return { ...prev, name: editableNameTrim };
                });
            } else {
                showErrorAlert(getAPIError(response, ACTION_FAIL_MESSAGE));
            }
        }, [editableName, eventName, eventUUID, setEventDetail]);

        const buttonText = useMemo(() => {
            if (isNameUpdating) {
                return "Updating";
            }
            if (isEditMode) {
                return "Update";
            }
            return (
                <>
                    <img
                        className="mr-2 ml-1"
                        src={editIcon}
                        alt="Edit Event Name"
                    />{" "}
                    Edit
                </>
            );
        }, [isNameUpdating, isEditMode]);

        const handleKeyPress = useCallback(
            (e: KeyboardEvent<HTMLInputElement>) => {
                if (e.key === "Enter" && isEditMode) {
                    e.preventDefault();
                    handleNameUpdate();
                }
            },
            [isEditMode, handleNameUpdate]
        );

        return (
            <div className="form-box mb-2 mt-3 mt-md-0 px-md-3">
                <Form>
                    <Form.Row className="align-items-center">
                        <Col md="2" className="d-flex align-items-center">
                            <Form.Label>Event Name:</Form.Label>
                        </Col>
                        <Col lg="8" md="7" xs="12">
                            <Form.Group className="mb-0">
                                <Form.Control
                                    type="text"
                                    required
                                    aria-label="name"
                                    name="eventName"
                                    value={editableName}
                                    onChange={(e) =>
                                        setEditableName(e.target.value)
                                    }
                                    readOnly={!isEditMode}
                                    onKeyPress={handleKeyPress}
                                />
                            </Form.Group>
                        </Col>
                        <Col lg="2" md="3" xs="12">
                            <HoverAuthorizeTooltip
                                permission="event:update"
                                className="w-100 mt-2 mt-md-0"
                            >
                                <Button
                                    type="button"
                                    variant={"primary"}
                                    className="w-100 px-sm-1 px-lg-1"
                                    onClick={
                                        isEditMode
                                            ? handleNameUpdate
                                            : handleEditClick
                                    }
                                    disabled={isNameUpdating || isActive}
                                >
                                    {buttonText}
                                </Button>
                            </HoverAuthorizeTooltip>
                        </Col>
                    </Form.Row>
                </Form>

                {!gateway_id && (
                    <Row className="mt-2">
                        <Col md="2" className="d-flex align-items-center">
                            <Form.Label>Event Type:</Form.Label>
                        </Col>
                        <Col>
                            <Box className="event-type">
                                <Typography variant="subtitle1">
                                    Global Event
                                </Typography>
                                <Typography variant="body2" color="#828FAB">
                                    Runs in the cloud, managing devices across
                                    all gateways. It supports a full range of
                                    actions including modify Event variables.
                                </Typography>
                            </Box>
                        </Col>
                    </Row>
                )}

                {gateway_id && (
                    <>
                        <Row className="mt-2">
                            <Col md="2" className="d-flex align-items-center">
                                <Form.Label>Event Type:</Form.Label>
                            </Col>
                            <Col>
                                <Box className="event-type">
                                    <Typography variant="subtitle1">
                                        Gateway Based Event
                                    </Typography>
                                    <Typography variant="body2" color="#828FAB">
                                        Operates locally on a specific gateway,
                                        monitoring and controlling only its
                                        connected devices. It offers faster
                                        response but excludes actions like
                                        modify Event variables.
                                    </Typography>
                                </Box>
                            </Col>
                        </Row>
                        <Row className="mt-2">
                            <Col md="2" className="d-flex align-items-center">
                                <Form.Label>Gateway:</Form.Label>
                            </Col>
                            <Col>
                                <Box className="event-type">
                                    <Typography variant="subtitle1">
                                        {gateway?.name}
                                    </Typography>
                                    <Typography variant="body2" color="#828FAB">
                                        UUID: {gateway?.gateway_id}
                                    </Typography>
                                </Box>
                            </Col>
                        </Row>
                    </>
                )}
            </div>
        );
    }
);
